import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Stack,
  Divider,
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ViewSidebarIcon from "@mui/icons-material/ViewSidebar";
import WebAssetIcon from "@mui/icons-material/WebAsset";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import CheckIcon from "@mui/icons-material/Check";
import { ThemeContext } from "../utils/ThemeContext";

const COLORS = [
  "#4F46E5",
  "#7C3AED",
  "#DB2777",
  "#F43F5E",
  "#F97316",
  "#F59E0B",
  "#10B981",
  "#0D9488",
  "#06B6D4",
  "#3B82F6",
];

const LIGHT_DEFAULTS = { sidebarColor: "#2c3e50", appBarColor: "#2f63ad", mode: "light" };
const DARK_DEFAULTS  = { sidebarColor: "#0d1b2a", appBarColor: "#1a3a5c", mode: "dark"  };

const ThemeSettings = () => {
  const { theme, updateTheme } = useContext(ThemeContext);

  const [selectedSidebar, setSelectedSidebar] = useState(theme.sidebarColor || LIGHT_DEFAULTS.sidebarColor);
  const [selectedAppbar, setSelectedAppbar]   = useState(theme.appBarColor  || LIGHT_DEFAULTS.appBarColor);
  const [mode, setMode] = useState(theme.mode || "light");

  // Sync local state when context changes (e.g. on mount from localStorage)
  useEffect(() => {
    setSelectedSidebar(theme.sidebarColor || LIGHT_DEFAULTS.sidebarColor);
    setSelectedAppbar(theme.appBarColor   || LIGHT_DEFAULTS.appBarColor);
    setMode(theme.mode || "light");
  }, [theme.sidebarColor, theme.appBarColor, theme.mode]);

  const handleModeChange = (newMode) => {
    setMode(newMode);
    const defaults = newMode === "dark" ? DARK_DEFAULTS : LIGHT_DEFAULTS;
    // Only reset sidebar/appbar to mode defaults if the user hasn't customised them
    const newTheme = {
      ...theme,
      mode: newMode,
      sidebarColor: selectedSidebar === (newMode === "dark" ? LIGHT_DEFAULTS.sidebarColor : DARK_DEFAULTS.sidebarColor)
        ? defaults.sidebarColor
        : selectedSidebar,
      appBarColor: selectedAppbar === (newMode === "dark" ? LIGHT_DEFAULTS.appBarColor : DARK_DEFAULTS.appBarColor)
        ? defaults.appBarColor
        : selectedAppbar,
    };
    updateTheme(newTheme);
  };

  const handleSidebarColor = (color) => {
    setSelectedSidebar(color);
    updateTheme({ sidebarColor: color });
  };

  const handleAppbarColor = (color) => {
    setSelectedAppbar(color);
    updateTheme({ appBarColor: color });
  };

  const handleReset = () => {
    const defaults = mode === "dark" ? DARK_DEFAULTS : LIGHT_DEFAULTS;
    setSelectedSidebar(defaults.sidebarColor);
    setSelectedAppbar(defaults.appBarColor);
    updateTheme(defaults);
  };

  const isDark = mode === "dark";

  // ── Styles ────────────────────────────────────────────────────────────────
  const pageCardSx = {
    maxWidth: 900,
    mx: "auto",
    mt: 1,
    p: { xs: 2, md: 3 },
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
  };

  const sectionCardSx = {
    borderRadius: "16px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
    p: { xs: 2, md: 2.5 },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    borderColor: "rgba(47,99,173,0.25)",
    color: "#2f63ad",
    px: 2.25,
  };

  const ColorBlock = ({ color, selected, onClick }) => (
    <Box
      onClick={onClick}
      sx={{
        width: 40,
        height: 40,
        borderRadius: "10px",
        backgroundColor: color,
        cursor: "pointer",
        border: selected ? "2px solid" : "1px solid rgba(0,0,0,0.08)",
        borderColor: selected ? "primary.main" : "rgba(0,0,0,0.08)",
        boxShadow: selected
          ? "0 0 0 3px rgba(47,99,173,0.18)"
          : "0 2px 8px rgba(0,0,0,0.08)",
        transform: selected ? "scale(1.1)" : "scale(1)",
        transition: "all 0.18s ease",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "&:hover": { transform: "scale(1.1)" },
      }}
    >
      {selected && <CheckIcon sx={{ fontSize: 16, color: "#fff", filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.5))" }} />}
    </Box>
  );

  const PreviewSwatch = ({ label, color, icon }) => (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: "14px",
        border: "1px solid rgba(47,99,173,0.08)",
        boxShadow: "0 4px 14px rgba(0,0,0,0.04)",
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.25 }}>
        <Box sx={{ color: "primary.main", display: "flex" }}>{icon}</Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
      </Stack>

      <Box
        sx={{
          height: 44,
          borderRadius: "10px",
          backgroundColor: color,
          border: "1px solid rgba(0,0,0,0.08)",
          mb: 1,
        }}
      />

      <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: "monospace" }}>
        {color}
      </Typography>
    </Paper>
  );

  return (
    <Paper elevation={0} sx={pageCardSx}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
          Theme Settings
        </Typography>
        <Typography variant="body2">
          Choose your display mode and customise the sidebar and top bar colours.
        </Typography>
      </Box>

      <Box sx={{ display: "grid", gap: 2.5 }}>

        {/* ── Mode toggle ─────────────────────────────────────────────────── */}
        <Paper elevation={0} sx={sectionCardSx}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            Display Mode
          </Typography>
          <Typography variant="body2" sx={{ mb: 2.5 }}>
            Switch between light and dark themes across the entire app.
          </Typography>

          <Grid container spacing={2}>
            {/* Light mode card */}
            <Grid item xs={12} sm={6}>
              <Box
                onClick={() => handleModeChange("light")}
                sx={{
                  borderRadius: "14px",
                  border: !isDark ? "2px solid" : "1px solid",
                  borderColor: !isDark ? "primary.main" : "divider",
                  cursor: "pointer",
                  overflow: "hidden",
                  transition: "all 0.2s ease",
                  boxShadow: !isDark
                    ? "0 0 0 3px rgba(47,99,173,0.15)"
                    : "0 2px 8px rgba(0,0,0,0.06)",
                  "&:hover": { borderColor: "primary.main" },
                }}
              >
                {/* Preview mockup */}
                <Box
                  sx={{
                    height: 100,
                    backgroundColor: "#f4f7fb",
                    display: "flex",
                    gap: 0,
                    overflow: "hidden",
                  }}
                >
                  {/* Sidebar strip */}
                  <Box sx={{ width: 32, backgroundColor: "#2c3e50", flexShrink: 0 }} />
                  {/* Content */}
                  <Box sx={{ flex: 1, p: 1, display: "flex", flexDirection: "column", gap: 0.75 }}>
                    <Box sx={{ height: 14, backgroundColor: "#2f63ad", borderRadius: "4px" }} />
                    <Box sx={{ height: 50, backgroundColor: "#fff", borderRadius: "8px", border: "1px solid rgba(47,99,173,0.1)" }} />
                    <Box sx={{ display: "flex", gap: 0.75 }}>
                      <Box sx={{ flex: 1, height: 20, backgroundColor: "#fff", borderRadius: "6px", border: "1px solid rgba(47,99,173,0.1)" }} />
                      <Box sx={{ flex: 1, height: 20, backgroundColor: "#fff", borderRadius: "6px", border: "1px solid rgba(47,99,173,0.1)" }} />
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <LightModeIcon sx={{ fontSize: 18, color: !isDark ? "primary.main" : "text.secondary" }} />
                    <Typography sx={{ fontWeight: !isDark ? 700 : 500, fontSize: "0.9rem" }}>
                      Light
                    </Typography>
                  </Stack>
                  {!isDark && (
                    <Box sx={{ width: 18, height: 18, borderRadius: "50%", backgroundColor: "primary.main", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <CheckIcon sx={{ fontSize: 12, color: "#fff" }} />
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>

            {/* Dark mode card */}
            <Grid item xs={12} sm={6}>
              <Box
                onClick={() => handleModeChange("dark")}
                sx={{
                  borderRadius: "14px",
                  border: isDark ? "2px solid" : "1px solid",
                  borderColor: isDark ? "primary.main" : "divider",
                  cursor: "pointer",
                  overflow: "hidden",
                  transition: "all 0.2s ease",
                  boxShadow: isDark
                    ? "0 0 0 3px rgba(47,99,173,0.15)"
                    : "0 2px 8px rgba(0,0,0,0.06)",
                  "&:hover": { borderColor: "primary.main" },
                }}
              >
                {/* Preview mockup */}
                <Box
                  sx={{
                    height: 100,
                    backgroundColor: "#0f1621",
                    display: "flex",
                    gap: 0,
                    overflow: "hidden",
                  }}
                >
                  {/* Sidebar strip */}
                  <Box sx={{ width: 32, backgroundColor: "#0d1b2a", flexShrink: 0 }} />
                  {/* Content */}
                  <Box sx={{ flex: 1, p: 1, display: "flex", flexDirection: "column", gap: 0.75 }}>
                    <Box sx={{ height: 14, backgroundColor: "#1a3a5c", borderRadius: "4px" }} />
                    <Box sx={{ height: 50, backgroundColor: "#16223a", borderRadius: "8px", border: "1px solid rgba(255,255,255,0.07)" }} />
                    <Box sx={{ display: "flex", gap: 0.75 }}>
                      <Box sx={{ flex: 1, height: 20, backgroundColor: "#16223a", borderRadius: "6px", border: "1px solid rgba(255,255,255,0.07)" }} />
                      <Box sx={{ flex: 1, height: 20, backgroundColor: "#16223a", borderRadius: "6px", border: "1px solid rgba(255,255,255,0.07)" }} />
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <DarkModeIcon sx={{ fontSize: 18, color: isDark ? "primary.main" : "text.secondary" }} />
                    <Typography sx={{ fontWeight: isDark ? 700 : 500, fontSize: "0.9rem" }}>
                      Dark
                    </Typography>
                  </Stack>
                  {isDark && (
                    <Box sx={{ width: 18, height: 18, borderRadius: "50%", backgroundColor: "primary.main", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <CheckIcon sx={{ fontSize: 12, color: "#fff" }} />
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <Divider />

        {/* ── Sidebar colour ───────────────────────────────────────────────── */}
        <Paper elevation={0} sx={sectionCardSx}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            Sidebar Colour
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Choose the main colour used for the sidebar navigation.
          </Typography>

          <Grid container spacing={1.25}>
            {COLORS.map((color) => (
              <Grid item key={color}>
                <ColorBlock
                  color={color}
                  selected={color === selectedSidebar}
                  onClick={() => handleSidebarColor(color)}
                />
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* ── Top bar colour ───────────────────────────────────────────────── */}
        <Paper elevation={0} sx={sectionCardSx}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            Top Bar Colour
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Choose the main colour used for the top navigation bar.
          </Typography>

          <Grid container spacing={1.25}>
            {COLORS.map((color) => (
              <Grid item key={color}>
                <ColorBlock
                  color={color}
                  selected={color === selectedAppbar}
                  onClick={() => handleAppbarColor(color)}
                />
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* ── Preview swatches ─────────────────────────────────────────────── */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <PreviewSwatch
              label="Sidebar Preview"
              color={selectedSidebar}
              icon={<ViewSidebarIcon fontSize="small" />}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <PreviewSwatch
              label="Top Bar Preview"
              color={selectedAppbar}
              icon={<WebAssetIcon fontSize="small" />}
            />
          </Grid>
        </Grid>

        {/* ── Actions ──────────────────────────────────────────────────────── */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            startIcon={<RestartAltIcon />}
            onClick={handleReset}
            sx={secondaryButtonSx}
          >
            Reset to Default
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default ThemeSettings;
